import React, { useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  useTheme,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import StaticPageLayout from "../components/StaticPageLayout";

const PrivacyPolicy = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <StaticPageLayout>
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 5 } }}>
      <Paper
        elevation={isDark ? 4 : 0}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 3,
          background: isDark
            ? "linear-gradient(145deg, #111927 0%, #0D1117 100%)"
            : "white",
          border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)",
        }}
      >

        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mb: 2 }}>
            <SecurityIcon 
              sx={{ 
                fontSize: 40, 
                color: "primary.lightGreen" 
              }} 
            />
            <Typography 
              variant="h2" 
              component="h1"
              sx={{ 
                fontWeight: 600,
                color: isDark ? "primary.headings" : "primary.headings",
              }}
            >
              Privacy Policy
            </Typography>
          </Box>

          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            Last Updated: December 31, 2025
          </Typography>
        </Box>

        <Divider sx={{ my: 5, borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)" }} />

        <Box component="section" sx={{ "& > section": { mb: 4 } }}>
         
          <section>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              {/* <InfoIcon color="primary" /> */}
              <Typography variant="h3" component="h2" color="primary.lightGreen">
                1. Introduction
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: "1rem" }}>
              Aikyam (“we,” “us,” or “our”) is committed to protecting your personal data and privacy across all our platforms. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our websites and services, 
              which are <strong>aikyam.dev</strong>, <strong>taskbnb.in</strong>, and <strong>autovertest.com</strong>.
            </Typography>
          </section>

          <section>
              <Typography variant="h4" component="h2" color="primary.lightGreen">
                2. Information We Collect
              </Typography>
            <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
              Personal Information:
            </Typography>
            <Box component="ul"  sx={{ pl: 4, mb: 3 }}>
              {["Name", "Email address", "Phone number", "Messages or inquiries"].map((item) => (
                <Typography component="li" key={item} variant="text" sx={{ mb: 1, fontSize: "1rem" }}>
                  {item}
                </Typography>
              ))}
            </Box>

            <Typography variant="h5" gutterBottom>
              Non-Personal Information:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              {["IP address", "Browser and device information", "Pages visited and usage statistics"].map((item) => (
                <Typography component="li" key={item} variant="text" sx={{ mb: 1, fontSize: "1rem" }}>
                  {item}
                </Typography>
              ))}
            </Box>
          </section>

          <section>
            <Typography variant="h4" component="h2" color="primary.lightGreen" gutterBottom>
              3. Use of Information
            </Typography>
            <Typography variant="h5">
              We use the collected information to:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mt: 1 }}>
              {[
                "Respond to your inquiries",
                "Improve our services",
                "Send updates or promotions ( with your consent)",
                "Fulfill legal obligations",
              ].map((item) => (
                <Typography component="li" key={item} variant="text" sx={{ mb: 1.2, fontSize: "1rem" }}>
                  {item}
                </Typography>
              ))}
            </Box>
          </section>

          <section>
            <Typography variant="h4" component="h2" color="primary.lightGreen" gutterBottom>
              4. Sharing of Information
            </Typography>
            <Typography variant="h5" paragraph>
              We do <strong>not</strong> sell or rent your personal data.
            </Typography>
            <Typography variant="h5" paragraph>
               Information may be shared with:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <Typography component="li" variant="text" sx={{ mb: 1, fontSize: "1rem" }}>Trusted service providers</Typography>
              <Typography component="li" variant="text" sx={{ mb: 1, fontSize: "1rem" }}>Legal authorities when required</Typography>
            </Box>
          </section>

          <section>
            <Typography variant="h4" component="h2" color="primary.lightGreen" gutterBottom>
              5. Cookies
            </Typography>
            <Typography variant="text" sx={{ fontSize: "1rem" }}>
             We use cookies to enhance user experience. You may disable cookies via browser settings. 
            </Typography>
          </section>

          <section>
            <Typography variant="h4" component="h2" color="primary.lightGreen" gutterBottom>
              6. Data Security
            </Typography>
            <Typography variant="text" sx={{ fontSize: "1rem" }}>
              We implement reasonable safeguards, but no system is fully secure. 
            </Typography>
          </section>

          <section>
            <Typography variant="h4" component="h2" color="primary.lightGreen" gutterBottom>
              7. Third-Party Links
            </Typography>
            <Typography variant="text" sx={{ fontSize: "1rem" }}>
              We are not responsible for third-party websites linked from our platforms. 
            </Typography>
          </section>

          <section>
            <Typography variant="h4" component="h2" color="primary.lightGreen" gutterBottom>
              8. Your Rights
            </Typography>
            <Typography variant="text" sx={{ fontSize: "1rem" }}>
           Depending on your location, you may request access, correction, deletion, or withdrawal of consent by contacting: 
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mt: 1, 
                fontWeight: 500, 
                fontSize: "1rem",
                color: "primary.lightGreen" 
              }}
            >
            <strong> Email:</strong> aikyamsolutionindia@gmail.com
            </Typography>
          </section>

          <section>
            <Typography variant="h4" component="h2" color="primary.lightGreen" gutterBottom>
              9. Policy Updates
            </Typography>
            <Typography variant="text" sx={{ fontSize: "1rem" }}>
              Updates will be posted with a revised effective date. 
            </Typography>
          </section>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: "center", color: "text.secondary" }}>
          <Typography variant="body1">
            © {new Date().getFullYear()} Aikyam Solutions. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Container>
    </StaticPageLayout>
  );
};

export default PrivacyPolicy;
